/**
 * @file AddFromAssetsModal.js
 * @description A specific style of action modal that includes title, subtitle, text, and action buttons.
 */

import style from './modals.module.scss';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../../assets/AssetLibrary.module.scss';
import AssetGridView from '../../assets/components/AssetGridView';
import NotFoundBox from '../no_data_indicators/NotFoundBox';
import PaginationComponent from '../../helpers/PaginationComponent';
import { SearchableDropdown } from '../../helpers/SearchableDropdown';
import Spinner from '../spinners/Spinner';
import Toast from '../toasts/Toast';
import { fetchAssets } from '../../../services/clientService';
import PopulationGroupMultiSelect from '../../shared/asyncSearchableMultiSelect/PopulationGroupMultiSelect';
import { getFileCategory } from '../../helpers/FileUtils';
import { getFileIcon } from '../svg/FileIcons';
import { getText } from '../../../i18n';

/**
 * AddFromAssetsModal component, rendering a styled modal for adding from assets.
 * @param {ModalConfig} modalProps - The properties for configuring the modal.
 * @returns {React.ReactElement} The rendered component.
 */
const AddFromAssetsModal = ({
    title,
    subtitle,
    onYes,
    onNo,
    onHide,
    yesButtonText,
    noButtonText,
    acceptedFileTypes,
    assetTypes = [3],
    wasCreatedByMe = true,
    requirePopulationGroupSelectionForImages = false,
    displayModalBackdrop = true,
    allowedFilterIds,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [activeCard, setActiveCard] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState([]);
    const [isPopulationGroupStep, setIsPopulationGroupStep] = useState(false);
    const [selectedPopulationGroups, setSelectedPopulationGroups] = useState(
        [],
    );

    const filterOptionsAssetLibrary = [
        { id: '5', name: 'Video' },
        { id: '6', name: 'Audio' },
        { id: '7', name: 'Image' },
        { id: '8', name: 'Document' },
        { id: '9', name: 'Spreadsheet' },
        { id: '1', name: 'Content Only' },
        { id: '4', name: 'Shared' },
        { id: '2', name: 'Unassigned' },
        { id: '13', name: 'Global' },
        { id: '14', name: 'Uploaded' },
    ];

    const resolvedFilterOptions = Array.isArray(allowedFilterIds)
        ? filterOptionsAssetLibrary.filter(option =>
              allowedFilterIds.includes(option.id),
          )
        : filterOptionsAssetLibrary;

    const selectedAsset = activeCard !== null ? assets[activeCard] : null;
    const selectedAssetCategory = selectedAsset
        ? getFileCategory(selectedAsset.fileType)
        : null;

    const renderSelectedAssetThumbnail = () => {
        if (!selectedAsset) {
            return <i className="bi bi-image" />;
        }

        if (selectedAssetCategory === 'image' && selectedAsset.url) {
            return <img src={selectedAsset.url} alt={selectedAsset.name} />;
        }

        return getFileIcon(selectedAsset.fileType);
    };

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const removeTag = filterIdToRemove => {
        setFilters(currentFilters =>
            currentFilters.filter(filter => filter.id !== filterIdToRemove),
        );
    };

    const hasSelectedFilter = filterId =>
        filters.some(item => item.id === filterId);

    const loadAssets = async () => {
        setIsLoading(true);
        try {
            let queryParams = {
                page: currentPage,
                pageSize: 12,
            };

            const assetTypeMapping = {
                1: 1,
                2: 2,
                3: 3,
            };

            const fileTypeMapping = {
                5: ['mp4', 'mov'],
                6: ['mp3', 'wav'],
                7: ['jpg', 'jpeg', 'png', 'svg'],
                8: ['docx', 'doc', 'txt', 'pdf', 'ppt', 'pptx'],
                9: ['xls', 'xlsx', 'csv', 'numbers'],
            };

            const flags = {
                4: 'isShared',
                10: 'isAssetUploader',
                11: 'isSharedToMe',
                12: 'isSharedByMe',
                13: 'isGlobalAsset',
                14: 'isClientUploaded',
            };

            const filteredAssetTypes = Object.keys(assetTypeMapping)
                .filter(hasSelectedFilter)
                .map(id => assetTypeMapping[id]);

            const baseAssetTypes = Array.isArray(assetTypes) ? assetTypes : [];

            if (filteredAssetTypes.length > 0) {
                queryParams = {
                    ...queryParams,
                    assetTypes: filteredAssetTypes,
                };
            } else if (baseAssetTypes.length > 0) {
                queryParams = { ...queryParams, assetTypes: baseAssetTypes };
            }

            const filteredFileTypes = Object.keys(fileTypeMapping)
                .filter(hasSelectedFilter)
                .flatMap(id => fileTypeMapping[id]);

            const baseFileTypes = Array.isArray(acceptedFileTypes)
                ? acceptedFileTypes.map(type => String(type).toLowerCase())
                : [];

            const resolvedFileTypes =
                filteredFileTypes.length > 0
                    ? baseFileTypes.length > 0
                        ? baseFileTypes.filter(type =>
                              filteredFileTypes.includes(type),
                          )
                        : filteredFileTypes
                    : baseFileTypes;

            if (
                filteredFileTypes.length > 0 &&
                baseFileTypes.length > 0 &&
                resolvedFileTypes.length === 0
            ) {
                setAssets([]);
                setTotalPages(1);
                return;
            }

            if (resolvedFileTypes.length > 0) {
                queryParams = { ...queryParams, fileTypes: resolvedFileTypes };
            }

            Object.keys(flags).forEach(id => {
                if (hasSelectedFilter(id)) {
                    queryParams = { ...queryParams, [flags[id]]: true };
                }
            });

            if (searchTerm !== '' && searchTerm !== ' ') {
                queryParams = { ...queryParams, search: searchTerm };
            }
            const fetchedAssets = await fetchAssets(
                queryParams,
                null,
                wasCreatedByMe,
            );
            setAssets(fetchedAssets.viewModels);
            setTotalPages(fetchedAssets.pageTotal);
        } catch (error) {
            console.error('Failed to fetch assets', error);
            Toast.error(getText('msg_cade149a85b2'));
            handleNo();
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadAssets();
    }, [currentPage, searchTerm, filters]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filters]);

    const handleClick = index => {
        setActiveCard(activeCard === index ? null : index);
        setIsPopulationGroupStep(false);
    };

    useEffect(() => {
        if (!selectedAsset) {
            setSelectedPopulationGroups([]);
            return;
        }

        // Step 2 should always start blank when adding from library.
        setSelectedPopulationGroups([]);
    }, [selectedAsset?.id]);

    const handleYes = () => {
        if (activeCard === null) {
            return Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
        }

        if (
            requirePopulationGroupSelectionForImages &&
            selectedAsset &&
            !isPopulationGroupStep
        ) {
            setIsPopulationGroupStep(true);
            return null;
        }

        const assetAdding = assets[activeCard];
        const selectedPopulationGroupIds = selectedPopulationGroups
            .map(group => group.id)
            .filter(Boolean)
            .map(groupId => +groupId || groupId);

        onYes({
            ...assetAdding,
            selectedPopulationGroups,
            selectedPopulationGroupIds,
        });
        onHide();
    };

    const handleNo = () => {
        onNo();
        onHide();
    };

    const hasActiveFilters = filters.length > 0;

    return (
        <div
            className={`${style.container} ${displayModalBackdrop ? 'modal-backdrop' : ''}`}
        >
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                aria-modal="true"
                role="dialog"
            >
                <div
                    className={`modal-dialog modal-lg modal-dialog-centered ${style.modalSizing}`}
                >
                    <div className={`modal-header ${style.modalHeader}`}></div>
                    <div className={`modal-content ${style.modalContent}`}>
                        <button
                            type="button"
                            className={`ing-text-buttons-save absolute-position pointer-cursor ${style.closeAction}`}
                            onClick={handleNo}
                            aria-label={getText('msg_bbfa773e5a63')}
                        >
                            <i className={`bi bi-x ${style.closeActionIcon}`} />
                            {getText('msg_bbfa773e5a63')}
                        </button>
                        <div
                            className={`modal-body ${style.modalBody} ${style.modalBodyTopAligned}`}
                        >
                            {title && (
                                <h5
                                    className={`modal-title ${style.title} fw-semibold`}
                                >
                                    {title}
                                </h5>
                            )}
                            {subtitle &&
                                (!requirePopulationGroupSelectionForImages ||
                                    isPopulationGroupStep) && (
                                    <h6 className={`${style.subtitle}`}>
                                        {subtitle}
                                    </h6>
                                )}

                            {!isPopulationGroupStep && (
                                <>
                                    <div className={style.assetSearchRow}>
                                        <div
                                            className={`${styles.searchWrapper} ${style.assetSearchWrapper}`}
                                        >
                                            <input
                                                type="text"
                                                className={`${styles.searchInput} w-100`}
                                                placeholder="Search Assets"
                                                value={searchTerm}
                                                onChange={handleSearch}
                                            />
                                            <span className={styles.searchIcon}>
                                                <i className="bi bi-search"></i>
                                            </span>
                                        </div>

                                        <div
                                            className={style.assetFilterWrapper}
                                        >
                                            <SearchableDropdown
                                                options={resolvedFilterOptions}
                                                optionIDField="id"
                                                savedOptions={filters}
                                                savedOptionIDField="id"
                                                updateOptions={setFilters}
                                                disabled={false}
                                                buttonMode={true}
                                                buttonLabel={'Filter'}
                                                buttonIcon={
                                                    'bi bi-filter-circle'
                                                }
                                                showTags={false}
                                            />
                                        </div>
                                    </div>

                                    {filters.length > 0 && (
                                        <div
                                            className={
                                                style.assetFilterTagsContainer
                                            }
                                        >
                                            {filters.map(filter => (
                                                <div
                                                    key={filter.id}
                                                    className={
                                                        style.assetFilterTag
                                                    }
                                                >
                                                    <i
                                                        className="bi bi-x"
                                                        onClick={() =>
                                                            removeTag(filter.id)
                                                        }
                                                    />
                                                    <span>{filter.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className={style.assetListScrollable}>
                                        {isLoading ? (
                                            <div
                                                className={
                                                    style.spinnerContainer
                                                }
                                            >
                                                <Spinner width={50} />
                                            </div>
                                        ) : (
                                            <>
                                                <AssetGridView
                                                    assets={assets}
                                                    propertiesModel={true}
                                                    handleClick={handleClick}
                                                    handleDoubleClick={() =>
                                                        null
                                                    }
                                                    activeCard={activeCard}
                                                    fullAssetName={false}
                                                />

                                                {assets.length < 1 && (
                                                    <NotFoundBox
                                                        backgroundColor="#f0f4ff"
                                                        title={
                                                            searchTerm ||
                                                            hasActiveFilters
                                                                ? 'Sorry, no results found'
                                                                : 'Sorry, there are no assets to display'
                                                        }
                                                        description={
                                                            searchTerm ||
                                                            hasActiveFilters
                                                                ? 'Try adjusting your search to find what you are looking for'
                                                                : `You can upload a New Asset by clicking "Cancel" on the bottom left corner, then clicking on "Upload from Device"`
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {!isLoading && (
                                        <PaginationComponent
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={setCurrentPage}
                                        />
                                    )}
                                </>
                            )}

                            {isPopulationGroupStep && selectedAsset && (
                                <div className={`${style.stepTwoBody} my-5`}>
                                    <div className={style.selectedAssetRow}>
                                        <div
                                            className={style.selectedAssetThumb}
                                        >
                                            {renderSelectedAssetThumbnail()}
                                        </div>

                                        <div
                                            className={style.selectedAssetMeta}
                                        >
                                            <h5
                                                className={
                                                    style.selectedAssetLabel
                                                }
                                            >
                                                {getText('msg_10e7ac23a20a')}
                                            </h5>
                                            <div
                                                className={`${style.selectedAssetValue} ${style.selectedAssetValueSpacing}`}
                                            >
                                                {selectedAsset.name}
                                            </div>
                                            <h5
                                                className={`${style.selectedAssetLabel} mt-4`}
                                            >
                                                {getText('msg_5023c8994cbd')}
                                            </h5>
                                            <div
                                                className={
                                                    style.selectedAssetValue
                                                }
                                            >
                                                {String(
                                                    selectedAsset.fileType ||
                                                        '',
                                                ).toUpperCase()}
                                            </div>
                                        </div>
                                    </div>

                                    <PopulationGroupMultiSelect
                                        selectedGroups={
                                            selectedPopulationGroups
                                        }
                                        onGroupsChange={
                                            setSelectedPopulationGroups
                                        }
                                        label={getText('msg_e744dd6b3c48')}
                                        isRequired={true}
                                        showSelectedItemsInField={true}
                                        showSelectionBox={true}
                                    />
                                </div>
                            )}

                            <div
                                className={`modal-footer ${style.modalFooter} ${style.modalFooterPinned} ${style.assetActionFooter}`}
                            >
                                <button
                                    type="button"
                                    className="button-base ing-popup-blue-negative"
                                    onClick={handleNo}
                                >
                                    {noButtonText}
                                </button>
                                <button
                                    type="button"
                                    className="button-base ing-button-primary-left"
                                    onClick={handleYes}
                                >
                                    {yesButtonText}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

AddFromAssetsModal.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    onYes: PropTypes.func.isRequired,
    onNo: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired,
    yesButtonText: PropTypes.string,
    noButtonText: PropTypes.string,
    acceptedFileTypes: PropTypes.array.isRequired,
    assetTypes: PropTypes.array,
    wasCreatedByMe: PropTypes.bool,
    requirePopulationGroupSelectionForImages: PropTypes.bool,
    allowedFilterIds: PropTypes.arrayOf(PropTypes.string),
};

export default AddFromAssetsModal;
